import React from 'react';

import { makeStyles, Theme } from '@material-ui/core/styles';
import {
  ExternalLink as ExternalLinkIcon,
  Secured as SecuredIcon,
} from 'components/icons';
import { FlipdishLogo } from 'components/icons/logos';
import { connect } from 'react-redux';

import { whitelabel } from '../../../core/state';
import * as Store from '../../../weborder/Store';
import { Typography } from '../../atoms';
import { Dialog } from '../../layouts';

const useStyles = makeStyles((theme: Theme) => ({
  aboutMyData: {
    '& .fd-text__h1': {
      marginBottom: theme.spacing(2),
    },
  },

  logoBanner: {
    alignItems: 'center',
    backgroundColor: '#041593',
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),

    '& svg': {
      color: theme.palette.common.white,
    },
    '& :first-child': {
      marginRight: theme.spacing(3),
    },
    '& :last-child': {
      marginRight: 0,
    },
  },

  secondaryText: {
    marginBottom: theme.spacing(3),

    '& .fd-text': {
      color: theme.palette.grey[800],
      marginBottom: theme.spacing(1),
    },
  },

  externalList: {
    listStyle: 'none',
    padding: 0,

    '& li': {
      borderTop: `1px solid ${theme.palette.grey[400]}`,
      transition: '0.2s',

      '&:last-child': {
        borderBottom: `1px solid ${theme.palette.grey[400]}`,
      },
    },

    '& a': {
      alignItems: 'center',
      boxSizing: 'border-box',
      color: theme.palette.common.black,
      display: 'flex',
      flexFlow: 'row nowrap',
      justifyContent: 'space-between',
      padding: theme.spacing(2.5, 0),
      textDecoration: 'none',
      width: '100%',

      '& .fd-icon': {
        marginRight: theme.spacing(1),
      },

      '& .fd-text': {
        fontWeight: theme.typography.fontWeightMedium,
        marginBottom: 0,
      },
    },
  },

  [theme.breakpoints.up('md')]: {
    aboutMyData: {
      '& .fd-text__h1': {
        marginBottom: theme.spacing(3),
      },
    },
  },
}));

type StateProps = {
  storeBrandName: string;
};
type OuterProps = {
  handleOnClick: React.MouseEventHandler<HTMLButtonElement>;
  showDialog: boolean;
  translate: any;
};
type Props = OuterProps & StateProps;

function AboutMyDataDialog(props: Props): JSX.Element {
  const classes = useStyles({});
  const { handleOnClick, showDialog, storeBrandName = '', translate } = props;

  return (
    <Dialog
      className={classes.aboutMyData}
      dialogName="about-data"
      onTitleClick={handleOnClick}
      open={showDialog}
    >
      <Typography variant="h1">{translate('About_my_data')}</Typography>

      <div className={classes.logoBanner}>
        <FlipdishLogo />
        <SecuredIcon size="m" />
      </div>

      <Typography>
        <em>
          {`${translate('we_use')} ${translate('Flipdish')} - ${translate(
            'global_secure_text',
          )}.`}
        </em>
      </Typography>

      <div className={classes.secondaryText}>
        <Typography>
          {translate('As_you_previously_ordered_from', {
            name: storeBrandName,
          })}
        </Typography>

        <Typography>
          {translate(
            'We_can_only_view_your_information_when_you_place_an_order',
          )}
        </Typography>
      </div>

      <ul className={classes.externalList}>
        <li>
          <a
            href="https://www.flipdish.com/flipdish-consumer-terms-and-conditions/"
            rel="noopener noreferrer"
            target="_blank"
          >
            <Typography>
              {translate('AboutMyDataDialog_Link_TermsOfService')}
            </Typography>
            <ExternalLinkIcon />
          </a>
        </li>
        <li>
          <a
            href="https://www.flipdish.com/privacy-policy/"
            rel="noopener noreferrer"
            target="_blank"
          >
            <Typography>
              {translate('AboutMyDataDialog_Link_PrivacyPolicy')}
            </Typography>
            <ExternalLinkIcon />
          </a>
        </li>
      </ul>
    </Dialog>
  );
}

const mapStateToProps = (state: Store.State): StateProps => {
  const appConfig = whitelabel.selectors.getAppConfig(state);

  return {
    storeBrandName: appConfig?.BrandName,
  };
};

export default connect(mapStateToProps)(AboutMyDataDialog);
export { AboutMyDataDialog };
